import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { ErrorHandlerService } from './error-handler.service';
import { PagingResponse } from '@models/paging-response.model';
import { LexbeDoc } from '../models/doc.model';
import { FileType } from 'src/app/models/file-type';

@Injectable({
	providedIn: 'root'
})
export class DocService {
	private dupOrderBy = '$orderby=isDuplicate,docTitle&$top=25';

	constructor(private http: HttpClient, private errorHandler: ErrorHandlerService, private appConfigService: AppConfigService) { }

	public getDocs(
		pageIndex: number,
		pageSize: number,
		sortBy?: string,
		filterId?: string,
		includeCustomCodingFields?: boolean,
		includeFolderPath?: boolean,
		includeCustodianName?: boolean,
		includeReviewerNames?: boolean,
		expandEmailFamily?: boolean,
		isSearch?: boolean,
		searchQuery?: string,
		saveSearch?: boolean
	): Observable<any> {

		let url = `${this.appConfigService.getConfig().webApi}/api2/LexbeDocs?pageIndex=${pageIndex}&pageSize=${pageSize}`;

		if (sortBy) url += `&sortBy=${sortBy}`;
		if (filterId) url += `&filterId=${filterId}`;
		if (includeCustomCodingFields) url += `&includeCustomCodingFields=${includeCustomCodingFields}`;
		if (includeFolderPath) url += `&includeFolderPath=${includeFolderPath}`;
		if (includeCustodianName) url += `&includeCustodianName=${includeCustodianName}`;
		if (includeReviewerNames) url += `&includeReviewerNames=${includeReviewerNames}`;
		if (expandEmailFamily) url += `&expandEmailFamily=${expandEmailFamily}`;
		if (isSearch) url += `&isSearch=${isSearch}`;
		if (searchQuery) {
			localStorage.setItem('searchQuery', searchQuery);
			url += `&searchQuery=${encodeURIComponent(searchQuery)}`;
		}
		if (saveSearch) url += `&saveSearch=${saveSearch}`;

		return this.http.get<any>(url).pipe(catchError(err => {
			this.errorHandler.handleError<any>(`get docs`);
			if (err?.status === 400 && err?.error?.message === 'Query is invalid or is not currently supported')
				return throwError('invalid query');
			else if (err?.status === 400 && err?.error?.message === 'No documents assigned to user.')
				return throwError('no-docs-error');
			return throwError('other exception');
		}));
	}

	public getDistinctDocSources(): Observable<string[]> {
		return this.http.get(`${this.appConfigService.getConfig().webApi}/api2/docs/docSources`).pipe(
			catchError(this.errorHandler.handleError<any>(`getDistinctDocSources`)));
	}

	public getDistinctDocTypes(): Observable<string[]> {
		return this.http.get(`${this.appConfigService.getConfig().webApi}/api2/docs/docTypes`).pipe(
			catchError(this.errorHandler.handleError<any>(`getDistinctDocTypes`)));
	}

	public getNearDupDocs(textGroupId: number): Observable<PagingResponse<LexbeDoc>> {
		return this.http.get(`${this.appConfigService.getConfig().webApi}/api2/docs/dupes?$filter=textGroupId eq ${textGroupId}&${this.dupOrderBy}`).pipe(
			catchError(this.errorHandler.handleError<any>(`getNearDupDocs`)));
	}

	public getExactDupDocs(dedupFamilyId: string, dedupHash: string): Observable<PagingResponse<LexbeDoc>> {
		return this.http.get(`${this.appConfigService.getConfig().webApi}/api2/docs/dupes?$filter=dedupFamilyId eq ${dedupFamilyId} and dedupHash eq '${dedupHash}'&${this.dupOrderBy}`).pipe(
			catchError(this.errorHandler.handleError<any>(`getExactDupDocs`)));
	}

	public getWordList(docId: string): Observable<string[]> {
		return this.http.get(`${this.appConfigService.getConfig().webApi}/api2/LexbeDocs/${docId}/WordList`).pipe(
			catchError(this.errorHandler.handleError<any>(`get word list`)));
	}

	public upload(file: File, fileType: FileType, docId: string) {
		const formData: FormData = new FormData();
		formData.append('File', file, file.name);
		formData.append('FileType', fileType.toString());
		formData.append('DocId', docId);

		return this.http.post(`${this.appConfigService.getConfig().webApi}/api2/DocUpload`, formData).pipe(
			map(() => {
				return true; // Upload succeeded without any issues (page counts match, body is parsed successfully)
			}),
			catchError(this.errorHandler.handleError<any>(`doc upload`)));
	}

	public delete(fileType: FileType, docId: string): Observable<any> {
		return this.http.delete(`${this.appConfigService.getConfig().webApi}/api2/LexbeDocs/${docId}/Files/${fileType}`).pipe(
			catchError(this.errorHandler.handleError<any>(`doc delete`)));
	}
}
